import { foldArabic } from '../quran/arabic';

/**
 * Comparing what a child recited against the āya they meant to recite — م-6-01.
 *
 * "يتلو الطالب في التطبيق قبل الحصة، فيُقارَن صوته بنص الآية وتُعلَّم المواضع
 * المخالفة."
 *
 * Everything this produces is a HINT. م-6-03 is explicit that the system's
 * notice never enters the official record — only an error a human معلم marked
 * does — so the type here is deliberately not the marker's type, cannot be
 * stored where marks are stored, and carries no muqriId. Nothing downstream
 * can mistake one for the other by shape.
 *
 * The bias throughout is towards SAYING NOTHING. م-6-07 puts a hard ceiling on
 * false errors — under 5% on real children's recitations — and a child told
 * they misread a word they read correctly learns to distrust the app and to
 * fear reciting. Silence costs a hint; a false accusation costs the point of
 * the product.
 */

/** One word of the āya, and what became of it. */
export type HintKind =
  /** Recited as written. */
  | 'ok'
  /** Something else was heard in its place. */
  | 'changed'
  /** Nothing was heard for it. */
  | 'skipped'
  /** Heard, but too uncertainly to say anything about. */
  | 'unclear';

export type PracticeHint = {
  /** 0-based index of the word within the āya. */
  word: number;
  expected: string;
  /** What the service thought it heard. Empty for a skipped word. */
  heard: string;
  kind: HintKind;
};

export type HeardWord = {
  text: string;
  /** 0..1 from the transcriber. Absent is treated as unknown, not as certain. */
  confidence?: number;
};

export type CompareInput = {
  /** The āya's words, in order, as written. */
  expected: string[];
  heard: HeardWord[];
  /** Below this, a word is 'unclear' rather than 'changed'. */
  minConfidence?: number;
};

export type Comparison = {
  hints: PracticeHint[];
  /** Words recited as written. */
  correct: number;
  /** Words the app is willing to say something about. */
  flagged: number;
  /**
   * True when the transcript diverges so far that the transcription itself is
   * the likely fault. The screen then says nothing about specific words.
   */
  unreliable: boolean;
};

/** Anything under this is not evidence a child said the wrong word. */
export const DEFAULT_MIN_CONFIDENCE = 0.6;

/**
 * Past this share of the āya diverging, the transcript is not describing this
 * recitation: a child reciting from memory does not miss two-thirds of an āya
 * and keep going. Naming words then would be inventing errors wholesale.
 */
const UNRELIABLE_ABOVE = 0.6;

/**
 * The skeleton to compare on.
 *
 * foldArabic alone is not enough here, and the reason is Uthmani orthography:
 * ٱلْعَٰلَمِينَ carries a DAGGER ALEF (U+0670) where ordinary spelling writes ا.
 * foldArabic deletes it along with the other marks — correct for a search box,
 * where nobody types it — leaving "العلمين", while any transcriber returns
 * "العالمين". Every such word compared as a mismatch, so the very first test
 * of a perfectly recited āya reported its last word wrong.
 *
 * The dagger alef therefore becomes a plain alef BEFORE folding, rather than
 * being dropped.
 */
const fold = (word: string): string => foldArabic(word.replace(/ٰ/g, 'ا'));

/**
 * The longest common subsequence of the two word lists, as index pairs.
 *
 * A diff rather than a position-by-position comparison: a child who adds or
 * misses one word shifts everything after it, and a naive comparison would
 * then report every remaining word as wrong — the single biggest source of
 * false errors, and exactly what م-6-07's ceiling forbids.
 */
function matchedPairs(expected: string[], heard: string[]): [number, number][] {
  const rows = expected.length;
  const cols = heard.length;
  const table: number[][] = Array.from({ length: rows + 1 }, () => new Array(cols + 1).fill(0));

  for (let i = rows - 1; i >= 0; i -= 1) {
    for (let j = cols - 1; j >= 0; j -= 1) {
      table[i][j] = expected[i] === heard[j]
        ? table[i + 1][j + 1] + 1
        : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  const pairs: [number, number][] = [];
  let i = 0;
  let j = 0;
  while (i < rows && j < cols) {
    if (expected[i] === heard[j]) { pairs.push([i, j]); i += 1; j += 1; }
    else if (table[i + 1][j] >= table[i][j + 1]) i += 1;
    else j += 1;
  }
  return pairs;
}

/**
 * One hint per word of the āya.
 *
 * Compared on the folded skeleton (see foldArabic): the transcript comes back
 * without diacritics, and comparing it against Uthmani orthography character
 * for character would mark every single word wrong.
 */
export function compareRecitation(input: CompareInput): Comparison {
  const { expected, heard } = input;
  const minConfidence = typeof input.minConfidence === 'number'
    ? input.minConfidence
    : DEFAULT_MIN_CONFIDENCE;

  if (expected.length === 0) {
    return { hints: [], correct: 0, flagged: 0, unreliable: false };
  }

  const foldedExpected = expected.map(fold);
  const foldedHeard = heard.map((w) => fold(w.text));

  const matched = new Map(matchedPairs(foldedExpected, foldedHeard));

  /*
   * A word the transcript did not match is 'changed' if something was heard
   * near it and 'skipped' if nothing was. "Near" is the span between the
   * matches on either side — the words the transcriber offered for this gap.
   */
  const hints: PracticeHint[] = expected.map((word, index) => {
    const at = matched.get(index);
    if (at !== undefined) {
      return { word: index, expected: word, heard: heard[at].text, kind: 'ok' as HintKind };
    }

    let before = -1;
    for (let k = index - 1; k >= 0; k -= 1) {
      const m = matched.get(k);
      if (m !== undefined) { before = m; break; }
    }
    let after = foldedHeard.length;
    for (let k = index + 1; k < expected.length; k += 1) {
      const m = matched.get(k);
      if (m !== undefined) { after = m; break; }
    }

    const gap = heard.slice(before + 1, after);
    if (gap.length === 0) {
      return { word: index, expected: word, heard: '', kind: 'skipped' as HintKind };
    }

    // The likeliest stand-in is the first thing heard in the gap.
    const candidate = gap[0];
    const sure = typeof candidate.confidence === 'number'
      && candidate.confidence >= minConfidence;
    return {
      word: index,
      expected: word,
      heard: candidate.text,
      kind: sure ? ('changed' as HintKind) : ('unclear' as HintKind),
    };
  });

  const correct = hints.filter((h) => h.kind === 'ok').length;
  const diverged = expected.length - correct;
  const unreliable = diverged / expected.length > UNRELIABLE_ABOVE;

  return {
    hints,
    correct,
    // 'unclear' is not flagged: the app has nothing to say about it.
    flagged: unreliable ? 0 : hints.filter((h) => h.kind === 'changed' || h.kind === 'skipped').length,
    unreliable,
  };
}

/**
 * The positions worth showing a معلم before the session — م-6-02.
 *
 * Ordered by position, never by "severity": a معلم reads along with the child,
 * and a list out of reading order is one they have to re-sort in their head.
 */
export function weakPositions(comparison: Comparison): PracticeHint[] {
  if (comparison.unreliable) return [];
  return comparison.hints.filter((h) => h.kind === 'changed' || h.kind === 'skipped');
}
